import { useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Star, Building, Users, Target } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import BrochureDownload from "@/components/BrochureDownload";
import Services from "@/components/Services";
import sapImage from "@/assets/1-sap-dashboard.jpg";
import sapEnterpriseBg from "@/assets/sap-enterprise-bg.jpg";
import { 
  DollarSign,
  BarChart3,
  Settings,
  Shield,
  Truck,
  Factory,
  FileText,
  Calendar,
  MessageSquare,
  TrendingUp,
  Database,
  Cog,
  Clock,
  Globe,
  UserCheck,
  BookOpen,
  Heart,
  Award,
  Zap,
  CheckCircle as Check,
  Layers,
  PieChart
} from "lucide-react";

interface SAPModuleData {
  // icon: any;
  title: string;
  // category: string;
  description: string;
  features: string[];
  technologies: string[];
  benefits: string[];
  process: string[];
  overview: string;
  implementation: {
    planning: string[];
    execution: string[];
    support: string[];
  };
  industries: string[];
  migrationTypes?: {
    type: string;
    description: string;
    caseStudy?: {
      client: string;
      challenge: string;
      solution: string;
      results: {
        metric: string;
        value: string;
        description: string;
      }[];
    };
    subtypes: {
      name: string;
      details: string;
      technicalSpecs?: string;
    }[];
  }[];
}

const sapModuleData: Record<string, SAPModuleData> = {
  "fi": {
    // icon: DollarSign,
    title: "SAP FI (Financial Accounting)",
    // category: "Finance",
    description: "Complete financial accounting solution with general ledger, accounts payable/receivable, and asset accounting.",
    overview: "SAP FI provides comprehensive financial accounting capabilities that enable organizations to manage their financial processes efficiently. From general ledger management to accounts payable and receivable, SAP FI forms the backbone of enterprise financial operations.",
    features: ["General Ledger Management", "Accounts Payable/Receivable", "Asset Accounting", "Bank Accounting", "Financial Reporting", "Multi-Currency Support"],
    technologies: ["SAP FI", "SAP New GL", "SAP Asset Accounting", "SAP Bank Communication Management", "SAP Financial Reporting", "SAP Analytics Cloud"],
    benefits: ["Real-time Financial Reporting", "Automated Reconciliation", "Compliance Management", "Multi-Currency Operations", "Integration with Other Modules", "Audit Trail Maintenance"],
    process: ["Requirements Analysis", "Chart of Accounts Design", "Configuration", "Data Migration", "Testing", "Go-Live Support"],
    implementation: {
      planning: ["Business Process Analysis", "Gap Analysis", "Technical Architecture", "Project Planning", "Resource Allocation"],
      execution: ["System Configuration", "Data Migration", "Integration Setup", "User Training", "Testing Phases"],
      support: ["Go-Live Support", "Post-Implementation Support", "Performance Optimization", "Ongoing Maintenance", "User Support"]
    },
    industries: ["Manufacturing", "Retail", "Healthcare", "Financial Services", "Public Sector", "Oil & Gas"]
  },
  "co": {
    // icon: PieChart,
    title: "SAP CO (Controlling)",
    // category: "Finance",
    description: "Cost center accounting, profit center analysis, and internal orders for comprehensive controlling.",
    overview: "SAP CO enables organizations to plan, report, and monitor their business operations. It provides detailed cost accounting, profitability analysis, and management reporting capabilities essential for strategic decision-making.",
    features: ["Cost Center Accounting", "Profit Center Analysis", "Internal Orders", "Product Costing", "Profitability Analysis", "Management Reporting"],
    technologies: ["SAP CO", "SAP Profitability Analysis", "SAP Product Cost Controlling", "SAP Management Accounting", "SAP Planning & Consolidation"],
    benefits: ["Detailed Cost Analysis", "Profitability Insights", "Budget Control", "Performance Monitoring", "Strategic Planning Support", "Real-time Reporting"],
    process: ["Controlling Structure Design", "Cost Element Configuration", "Cost Center Setup", "Product Costing", "Reporting Configuration", "User Training"],
    implementation: {
      planning: ["Controlling Requirements", "Organization Structure", "Costing Methodology", "Reporting Strategy", "Integration Planning"],
      execution: ["Master Data Setup", "Configuration", "Cost Allocation Rules", "Reporting Development", "Testing"],
      support: ["Month-end Support", "Reporting Optimization", "Process Improvements", "User Training", "System Maintenance"]
    },
    industries: ["Manufacturing", "Chemicals", "Automotive", "Consumer Goods", "Pharmaceuticals", "Technology"]
  },
  "mm": {
    // icon: BarChart3,
    title: "SAP MM (Materials Management)",
    // category: "Operations",
    description: "Procurement, inventory management, and vendor evaluation for materials management.",
    overview: "SAP MM streamlines procurement processes and inventory management, providing end-to-end visibility into materials flow and costs. It integrates purchasing, inventory, and vendor management into a unified system.",
    features: ["Procurement Processing", "Inventory Management", "Vendor Evaluation", "Purchase Requisitions", "Material Planning", "Invoice Verification"],
    technologies: ["SAP MM", "SAP SRM", "SAP Ariba", "SAP Invoice Management", "SAP Integrated Business Planning", "SAP S/4HANA Sourcing"],
    benefits: ["Streamlined Procurement", "Inventory Optimization", "Cost Savings", "Vendor Performance Management", "Compliance Tracking", "Real-time Visibility"],
    process: ["Material Master Setup", "Vendor Master Configuration", "Purchasing Configuration", "Inventory Setup", "Integration Testing", "User Training"],
    implementation: {
      planning: ["Procurement Strategy", "Material Classification", "Vendor Strategy", "Approval Workflows", "Integration Requirements"],
      execution: ["Master Data Creation", "Procurement Configuration", "Workflow Setup", "Integration Development", "Testing Phases"],
      support: ["Procurement Support", "Vendor Onboarding", "Process Optimization", "Performance Monitoring", "Continuous Improvement"]
    },
    industries: ["Manufacturing", "Retail", "Aerospace", "Defense", "Mining", "Construction"]
  },
  "pp": {
    // icon: Factory,
    title: "SAP PP (Production Planning)",
    // category: "Manufacturing",
    description: "Manufacturing execution, capacity planning, and shop floor control for production optimization.",
    overview: "SAP PP provides comprehensive production planning and manufacturing execution capabilities. It enables organizations to optimize their production processes, manage capacity, and ensure efficient shop floor operations.",
    features: ["Production Planning", "Capacity Planning", "Shop Floor Control", "Manufacturing Execution", "Material Requirements Planning", "Production Orders"],
    technologies: ["SAP PP", "SAP Manufacturing Execution", "SAP Capacity Planning", "SAP Advanced Planning", "SAP Digital Manufacturing"],
    benefits: ["Optimized Production", "Reduced Lead Times", "Improved Quality", "Resource Optimization", "Real-time Monitoring", "Cost Control"],
    process: ["Production Structure Design", "Capacity Planning", "Shop Floor Configuration", "Integration Setup", "Testing", "Go-Live"],
    implementation: {
      planning: ["Production Strategy", "Capacity Analysis", "Resource Planning", "Shop Floor Design", "Integration Planning"],
      execution: ["Master Data Setup", "Production Configuration", "Shop Floor Setup", "Integration Development", "User Training"],
      support: ["Production Support", "Performance Optimization", "Process Improvements", "Training Programs", "System Maintenance"]
    },
    industries: ["Automotive", "Aerospace", "Electronics", "Pharmaceuticals", "Food & Beverage", "Chemicals"]
  },
  "sd": {
    // icon: Truck,
    title: "SAP SD (Sales & Distribution)",
    // category: "Sales",
    description: "Order management, pricing, shipping, and billing for comprehensive sales operations.",
    overview: "SAP SD manages the entire sales process from inquiry to delivery and billing. It provides comprehensive functionality for order processing, pricing, shipping, and customer management.",
    features: ["Order Management", "Pricing Configuration", "Shipping Processing", "Billing & Invoicing", "Customer Management", "Credit Management"],
    technologies: ["SAP SD", "SAP Variant Configuration", "SAP Transportation Management", "SAP Customer Relationship Management", "SAP Commerce"],
    benefits: ["Streamlined Sales Process", "Automated Pricing", "Efficient Order Processing", "Customer Satisfaction", "Revenue Optimization", "Real-time Tracking"],
    process: ["Sales Structure Design", "Pricing Configuration", "Order Processing Setup", "Shipping Configuration", "Billing Setup", "User Training"],
    implementation: {
      planning: ["Sales Strategy", "Customer Segmentation", "Pricing Strategy", "Distribution Channels", "Integration Requirements"],
      execution: ["Master Data Setup", "Sales Configuration", "Pricing Setup", "Shipping Configuration", "Testing"],
      support: ["Sales Support", "Order Processing", "Customer Service", "Performance Monitoring", "Process Optimization"]
    },
    industries: ["Retail", "Manufacturing", "Distribution", "Consumer Goods", "Automotive", "Technology"]
  },
  "hr": {
    // icon: Users,
    title: "SAP HR/HCM (Human Capital)",
    // category: "HR",
    description: "Personnel administration, payroll, and talent management for human resources.",
    overview: "SAP HR/HCM provides comprehensive human capital management capabilities, from personnel administration to payroll processing and talent management. It enables organizations to manage their workforce effectively.",
    features: ["Personnel Administration", "Payroll Processing", "Talent Management", "Time Management", "Benefits Administration", "Performance Management"],
    technologies: ["SAP HCM", "SAP SuccessFactors", "SAP Time Management", "SAP Payroll", "SAP Learning Solution"],
    benefits: ["Efficient HR Processes", "Compliance Management", "Employee Self-Service", "Talent Development", "Cost Control", "Data Analytics"],
    process: ["HR Strategy Definition", "Organizational Structure", "Payroll Configuration", "Time Management Setup", "Benefits Configuration", "Training"],
    implementation: {
      planning: ["HR Strategy", "Organizational Design", "Payroll Requirements", "Compliance Requirements", "Integration Planning"],
      execution: ["Master Data Setup", "HR Configuration", "Payroll Setup", "Time Management", "Benefits Configuration"],
      support: ["Payroll Support", "HR Operations", "Employee Support", "Compliance Monitoring", "System Maintenance"]
    },
    industries: ["All Industries", "Public Sector", "Healthcare", "Education", "Financial Services", "Manufacturing"]
  },
  "bw": {
    // icon: Database,
    title: "SAP BW (Business Warehouse)",
    // category: "Analytics",
    description: "Data warehousing, reporting, and business intelligence for data-driven decisions.",
    overview: "SAP BW provides comprehensive data warehousing and business intelligence capabilities, enabling organizations to consolidate data from multiple sources and generate meaningful insights for strategic decision-making.",
    features: ["Data Warehousing", "ETL Processing", "Report Generation", "Analytics Dashboards", "Data Mining", "OLAP Analysis"],
    technologies: ["SAP BW/4HANA", "SAP Analytics Cloud", "SAP Data Services", "SAP BusinessObjects", "SAP Lumira"],
    benefits: ["Centralized Data", "Real-time Analytics", "Self-Service BI", "Scalable Architecture", "Integrated Reporting", "Data Quality"],
    process: ["Data Architecture Design", "ETL Development", "Data Modeling", "Report Creation", "Dashboard Design", "User Training"],
    implementation: {
      planning: ["Data Strategy", "Source System Analysis", "Architecture Design", "ETL Planning", "Reporting Requirements"],
      execution: ["Data Warehouse Setup", "ETL Development", "Data Modeling", "Report Development", "Dashboard Creation"],
      support: ["Data Quality Monitoring", "Performance Optimization", "Report Maintenance", "User Support", "System Updates"]
    },
    industries: ["Manufacturing", "Retail", "Financial Services", "Healthcare", "Telecommunications", "Public Sector"]
  },
  "crm": {
    // icon: Globe,
    title: "SAP CRM (Customer Relationship)",
    // category: "CRM",
    description: "Sales force automation, marketing campaigns, and service management for customer relations.",
    overview: "SAP CRM provides comprehensive customer relationship management capabilities, enabling organizations to manage sales processes, marketing campaigns, and service operations in an integrated environment.",
    features: ["Sales Force Automation", "Marketing Campaigns", "Service Management", "Customer Analytics", "Interaction Center", "Partner Channel Management"],
    technologies: ["SAP CRM", "SAP Marketing Cloud", "SAP Service Cloud", "SAP Sales Cloud", "SAP Customer Data Platform"],
    benefits: ["360-Degree Customer View", "Improved Sales Performance", "Enhanced Customer Service", "Marketing Automation", "Mobile Access", "Real-time Analytics"],
    process: ["CRM Strategy Definition", "Customer Data Management", "Sales Process Configuration", "Marketing Setup", "Service Configuration", "User Training"],
    implementation: {
      planning: ["CRM Strategy", "Customer Journey Mapping", "Process Design", "Integration Planning", "Data Migration Planning"],
      execution: ["System Configuration", "Data Migration", "Process Implementation", "Integration Development", "User Interface Setup"],
      support: ["Go-Live Support", "User Training", "Process Optimization", "Performance Monitoring", "Continuous Improvement"]
    },
    industries: ["Retail", "Financial Services", "Manufacturing", "Telecommunications", "Healthcare", "Automotive"]
  },
  "basis": {
    // icon: Settings,
    title: "SAP BASIS (Technical Foundation)",
    // category: "Technical",
    description: "System administration, performance tuning, and security for SAP infrastructure.",
    overview: "SAP BASIS provides the technical foundation for all SAP systems, managing system administration, database management, security, and performance optimization to ensure reliable SAP operations.",
    features: ["System Administration", "Performance Tuning", "Database Management", "Transport Management", "Security Management", "Monitoring"],
    technologies: ["SAP BASIS", "SAP Solution Manager", "SAP HANA Administration", "SAP Cloud Platform", "SAP Landscape Management"],
    benefits: ["System Reliability", "Optimized Performance", "Enhanced Security", "Automated Operations", "Proactive Monitoring", "Compliance Management"],
    process: ["Infrastructure Assessment", "System Installation", "Configuration", "Security Setup", "Monitoring Implementation", "Documentation"],
    implementation: {
      planning: ["Infrastructure Planning", "Architecture Design", "Security Framework", "Monitoring Strategy", "Backup Planning"],
      execution: ["System Installation", "Configuration", "Security Implementation", "Monitoring Setup", "Transport Setup"],
      support: ["System Monitoring", "Performance Optimization", "Security Updates", "Backup Management", "Issue Resolution"]
    },
    industries: ["All Industries", "Enterprise Organizations", "Large Corporations", "Multi-national Companies", "Government", "Manufacturing"]
  },
  "security": {
    // icon: Shield,
    title: "SAP Security",
    // category: "Security",
    description: "User management, authorization, and audit compliance for SAP security.",
    overview: "SAP Security ensures comprehensive protection of SAP systems through user management, role-based authorization, and audit compliance, maintaining data integrity and regulatory compliance.",
    features: ["User Management", "Role-based Authorization", "Audit Compliance", "GRC Implementation", "Single Sign-On", "Identity Management"],
    technologies: ["SAP GRC", "SAP Identity Management", "SAP Authorization", "SAP Access Control", "SAP Single Sign-On"],
    benefits: ["Enhanced Security", "Compliance Assurance", "Risk Mitigation", "Simplified Access Management", "Audit Trail", "Fraud Prevention"],
    process: ["Security Assessment", "Role Design", "Authorization Implementation", "Audit Setup", "Compliance Configuration", "User Training"],
    implementation: {
      planning: ["Security Strategy", "Risk Assessment", "Compliance Requirements", "Role Framework Design", "Integration Planning"],
      execution: ["Security Configuration", "Role Implementation", "GRC Setup", "Audit Configuration", "Testing"],
      support: ["Security Monitoring", "Role Maintenance", "Compliance Reporting", "Risk Management", "Incident Response"]
    },
    industries: ["Financial Services", "Healthcare", "Government", "Manufacturing", "Retail", "Energy"]
  },
  "dms": {
    // icon: FileText,
    title: "SAP Document Management",
    // category: "Document",
    description: "Document storage, workflow, and archiving solutions for document management.",
    overview: "SAP Document Management provides comprehensive document lifecycle management with storage, workflow automation, and archiving capabilities for efficient document handling across the organization.",
    features: ["Document Storage", "Workflow Automation", "Archive Management", "Content Repository", "Version Control", "Document Security"],
    technologies: ["SAP DMS", "SAP Content Server", "SAP Archive Development Kit", "SAP Extended ECM", "SAP Document Builder"],
    benefits: ["Centralized Document Storage", "Automated Workflows", "Compliance Management", "Version Control", "Secure Access", "Cost Reduction"],
    process: ["Document Strategy", "Repository Design", "Workflow Configuration", "Archive Setup", "Security Implementation", "User Training"],
    implementation: {
      planning: ["Document Strategy", "Repository Architecture", "Workflow Design", "Security Framework", "Migration Planning"],
      execution: ["Repository Setup", "Workflow Configuration", "Document Migration", "Security Implementation", "Integration"],
      support: ["Document Management", "Workflow Monitoring", "Archive Maintenance", "User Support", "System Optimization"]
    },
    industries: ["Manufacturing", "Automotive", "Aerospace", "Pharmaceuticals", "Energy", "Construction"]
  },
  "workflow": {
    title: "SAP Workflow",
    description: "Business process automation and approval workflows for streamlined operations.",
    overview: "SAP Workflow enables business process automation with intelligent routing, approval mechanisms, and task management, streamlining operations and improving efficiency across the organization.",
    features: ["Process Automation", "Approval Workflows", "Task Management", "Event Processing", "Deadline Management", "Escalation Handling"],
    technologies: ["SAP Workflow", "SAP Business Workflow", "SAP Process Integration", "SAP Workflow Builder", "SAP Business Rule Framework"],
    benefits: ["Process Efficiency", "Reduced Manual Work", "Faster Approvals", "Compliance Assurance", "Transparency", "Cost Savings"],
    process: ["Process Analysis", "Workflow Design", "Configuration", "Testing", "Deployment", "User Training"],
    implementation: {
      planning: ["Process Mapping", "Workflow Design", "Approval Matrix", "Integration Requirements", "Performance Planning"],
      execution: ["Workflow Configuration", "Agent Assignment", "Testing", "Integration Setup", "Go-Live Preparation"],
      support: ["Workflow Monitoring", "Performance Optimization", "Issue Resolution", "Process Improvements", "User Support"]
    },
    industries: ["All Industries", "Financial Services", "Manufacturing", "Public Sector", "Healthcare", "Utilities"]
  },
  "srm": {
    // icon: Target,
    title: "SAP SRM (Supplier Relationship)",
    // category: "Procurement",
    description: "Vendor management, sourcing, and contract management for supplier relationships.",
    overview: "SAP SRM optimizes supplier relationships through strategic sourcing, contract management, and vendor performance monitoring, enabling organizations to achieve procurement excellence.",
    features: ["Vendor Management", "Strategic Sourcing", "Contract Management", "Supplier Evaluation", "Spend Analysis", "Procurement Analytics"],
    technologies: ["SAP SRM", "SAP Ariba", "SAP Fieldglass", "SAP Concur", "SAP Analytics Cloud"],
    benefits: ["Cost Savings", "Supplier Performance", "Risk Management", "Compliance", "Process Efficiency", "Strategic Sourcing"],
    process: ["Sourcing Strategy", "Vendor Onboarding", "Contract Management", "Performance Monitoring", "Analytics Setup", "User Training"],
    implementation: {
      planning: ["Procurement Strategy", "Supplier Strategy", "Contract Framework", "Performance Metrics", "Integration Planning"],
      execution: ["System Configuration", "Vendor Onboarding", "Contract Setup", "Performance Monitoring", "Analytics Implementation"],
      support: ["Vendor Management", "Contract Monitoring", "Performance Reviews", "Process Optimization", "Continuous Improvement"]
    },
    industries: ["Manufacturing", "Retail", "Automotive", "Aerospace", "Oil & Gas", "Pharmaceuticals"]
  },
  "real-estate": {
    // icon: Building,
    title: "SAP Real Estate",
    // category: "Real Estate",
    description: "Property management, lease administration, and space management for real estate operations.",
    overview: "SAP Real Estate provides comprehensive real estate management capabilities, from property portfolio management to lease administration and space utilization, optimizing real estate operations.",
    features: ["Property Management", "Lease Administration", "Space Management", "Facility Management", "Cost Management", "Real Estate Analytics"],
    technologies: ["SAP Real Estate Management", "SAP Facility Management", "SAP Analytics Cloud", "SAP Mobile"],
    benefits: ["Optimized Space Utilization", "Cost Control", "Lease Management", "Compliance", "Facility Optimization", "Strategic Planning"],
    process: ["Real Estate Strategy", "Property Setup", "Lease Configuration", "Space Planning", "Cost Allocation", "Reporting Setup"],
    implementation: {
      planning: ["Real Estate Strategy", "Property Portfolio Analysis", "Space Planning", "Cost Framework", "Integration Requirements"],
      execution: ["Property Configuration", "Lease Setup", "Space Management", "Cost Allocation", "Reporting Implementation"],
      support: ["Property Management", "Lease Monitoring", "Space Optimization", "Cost Analysis", "Strategic Planning"]
    },
    industries: ["Corporate Real Estate", "Government", "Healthcare", "Education", "Retail", "Manufacturing"]
  },
  "healthcare": {
    // icon: Heart,
    title: "SAP Healthcare",
    // category: "Healthcare",
    description: "Patient management, medical billing, and clinical workflows for healthcare industry.",
    overview: "SAP Healthcare delivers industry-specific solutions for healthcare organizations, managing patient data, clinical processes, and financial operations with compliance and efficiency.",
    features: ["Patient Management", "Medical Billing", "Clinical Workflows", "Healthcare Analytics", "Regulatory Compliance", "Revenue Cycle Management"],
    technologies: ["SAP for Healthcare", "SAP Medical Research Insights", "SAP Health Information Exchange", "SAP Patient Management"],
    benefits: ["Improved Patient Care", "Operational Efficiency", "Regulatory Compliance", "Cost Management", "Data Integration", "Clinical Insights"],
    process: ["Healthcare Strategy", "Patient Data Management", "Clinical Process Design", "Billing Setup", "Compliance Configuration", "Training"],
    implementation: {
      planning: ["Healthcare Strategy", "Clinical Process Analysis", "Compliance Requirements", "Data Integration", "Performance Metrics"],
      execution: ["System Configuration", "Clinical Process Setup", "Billing Implementation", "Compliance Setup", "Data Integration"],
      support: ["Patient Management", "Clinical Support", "Billing Operations", "Compliance Monitoring", "Performance Optimization"]
    },
    industries: ["Hospitals", "Healthcare Systems", "Medical Centers", "Clinics", "Healthcare Networks", "Government Healthcare"]
  },
  "education": {
    // icon: BookOpen,
    title: "SAP Education",
    // category: "Education",
    description: "Student information systems and academic planning for educational institutions.",
    overview: "SAP Education provides comprehensive solutions for educational institutions, managing student information, academic processes, and administrative operations with efficiency and compliance.",
    features: ["Student Information Systems", "Academic Planning", "Course Management", "Student Analytics", "Financial Aid Management", "Alumni Management"],
    technologies: ["SAP for Higher Education", "SAP Student Lifecycle Management", "SAP Analytics Cloud", "SAP Mobile"],
    benefits: ["Student Success", "Operational Efficiency", "Data-driven Decisions", "Compliance Management", "Cost Optimization", "Stakeholder Engagement"],
    process: ["Education Strategy", "Student Data Management", "Academic Process Design", "System Configuration", "Analytics Setup", "Training"],
    implementation: {
      planning: ["Education Strategy", "Student Journey Mapping", "Academic Process Analysis", "Data Requirements", "Integration Planning"],
      execution: ["System Configuration", "Student Data Setup", "Academic Process Implementation", "Analytics Configuration", "Testing"],
      support: ["Student Services", "Academic Operations", "Data Management", "Performance Monitoring", "Continuous Improvement"]
    },
    industries: ["Universities", "Colleges", "K-12 Schools", "Vocational Schools", "Training Institutes", "Government Education"]
  },
  "netweaver": {
    // icon: Layers,
    title: "SAP NetWeaver",
    // category: "Integration",
    description: "Integration platform, portal technology, and master data management for SAP landscapes.",
    overview: "SAP NetWeaver provides the technical foundation for SAP applications with integration, portal, and development capabilities, enabling seamless connectivity across SAP and non-SAP systems.",
    features: ["Integration Platform", "Portal Technology", "Master Data Management", "Web Services", "Development Platform", "Business Intelligence"],
    technologies: ["SAP NetWeaver", "SAP Process Integration", "SAP Master Data Management", "SAP Enterprise Portal", "SAP Web Dynpro"],
    benefits: ["Seamless Integration", "Unified Portal", "Master Data Consistency", "Development Efficiency", "Service-Oriented Architecture", "Platform Independence"],
    process: ["Integration Architecture", "Portal Design", "Master Data Strategy", "Development Framework", "Testing", "Deployment"],
    implementation: {
      planning: ["Integration Strategy", "Portal Architecture", "Master Data Framework", "Development Standards", "Performance Planning"],
      execution: ["Platform Installation", "Integration Configuration", "Portal Development", "Master Data Setup", "Testing"],
      support: ["Integration Monitoring", "Portal Maintenance", "Master Data Management", "Performance Optimization", "Platform Updates"]
    },
    industries: ["All Industries", "Large Enterprises", "Multi-system Environments", "Global Organizations", "Integration-heavy Businesses"]
  },
  "businessintelligence": {
    // icon: TrendingUp,
    title: "SAP Business Intelligence",
    // category: "Analytics",
    description: "Analytics, dashboards, and performance management for business intelligence.",
    overview: "SAP Business Intelligence provides comprehensive analytics and reporting capabilities, enabling organizations to transform data into actionable insights for strategic decision-making.",
    features: ["Business Analytics", "Performance Dashboards", "KPI Management", "Predictive Analytics", "Self-Service BI", "Mobile Analytics"],
    technologies: ["SAP BusinessObjects", "SAP Analytics Cloud", "SAP Lumira", "SAP Crystal Reports", "SAP Web Intelligence"],
    benefits: ["Data-driven Decisions", "Real-time Insights", "Self-Service Analytics", "Mobile Access", "Predictive Intelligence", "Cost Optimization"],
    process: ["BI Strategy", "Data Preparation", "Report Development", "Dashboard Design", "Analytics Implementation", "User Training"],
    implementation: {
      planning: ["BI Strategy", "Data Architecture", "Reporting Requirements", "Dashboard Design", "User Requirements"],
      execution: ["Platform Setup", "Data Integration", "Report Development", "Dashboard Creation", "Testing"],
      support: ["Report Maintenance", "Dashboard Updates", "Performance Monitoring", "User Support", "Analytics Enhancement"]
    },
    industries: ["All Industries", "Financial Services", "Manufacturing", "Retail", "Healthcare", "Government"]
  },
  "time": {
    // icon: Clock,
    title: "SAP Time Management",
    // category: "HR",
    description: "Time tracking, attendance, and shift planning for workforce management.",
    overview: "SAP Time Management provides comprehensive time and attendance tracking with automated calculations, shift planning, and integration with payroll and HR processes.",
    features: ["Time Tracking", "Attendance Management", "Shift Planning", "Leave Management", "Overtime Management", "Time Analytics"],
    technologies: ["SAP Time Management", "SAP SuccessFactors Time Tracking", "SAP Mobile", "SAP Analytics Cloud"],
    benefits: ["Accurate Time Tracking", "Automated Calculations", "Compliance Management", "Mobile Access", "Cost Control", "Productivity Insights"],
    process: ["Time Policy Design", "Shift Planning", "Attendance Setup", "Integration Configuration", "Testing", "User Training"],
    implementation: {
      planning: ["Time Management Strategy", "Policy Framework", "Shift Requirements", "Integration Planning", "Compliance Requirements"],
      execution: ["System Configuration", "Policy Setup", "Shift Configuration", "Device Setup", "Integration Development"],
      support: ["Time Administration", "Policy Maintenance", "System Monitoring", "User Support", "Compliance Reporting"]
    },
    industries: ["Manufacturing", "Retail", "Healthcare", "Transportation", "Construction", "Government"]
  },
  "qm": {
    // icon: Award,
    title: "SAP Quality Management",
    // category: "Quality",
    description: "Quality planning, inspection, and certificate management for quality assurance.",
    overview: "SAP Quality Management ensures product and service quality through comprehensive quality planning, inspection processes, and certificate management across the entire value chain.",
    features: ["Quality Planning", "Inspection Management", "Certificate Management", "Quality Analytics", "Non-conformance Management", "Audit Management"],
    technologies: ["SAP QM", "SAP Quality Management in EHS", "SAP Analytics Cloud", "SAP Mobile"],
    benefits: ["Quality Assurance", "Compliance Management", "Risk Mitigation", "Cost Reduction", "Customer Satisfaction", "Continuous Improvement"],
    process: ["Quality Strategy", "Inspection Planning", "Certificate Setup", "Analytics Configuration", "Testing", "Training"],
    implementation: {
      planning: ["Quality Strategy", "Inspection Framework", "Certificate Requirements", "Analytics Planning", "Compliance Requirements"],
      execution: ["QM Configuration", "Inspection Setup", "Certificate Management", "Analytics Implementation", "Testing"],
      support: ["Quality Operations", "Inspection Management", "Certificate Maintenance", "Performance Monitoring", "Continuous Improvement"]
    },
    industries: ["Manufacturing", "Automotive", "Pharmaceuticals", "Food & Beverage", "Aerospace", "Medical Devices"]
  },
  "utilities": {
    // icon: Zap,
    title: "SAP Energy & Utilities",
    // category: "Industry",
    description: "Meter reading, billing, and customer service for energy and utilities sector.",
    overview: "SAP Energy & Utilities provides industry-specific solutions for utilities companies, managing meter reading, billing, customer service, and regulatory compliance.",
    features: ["Meter Reading", "Utility Billing", "Customer Service", "Energy Trading", "Regulatory Compliance", "Grid Management"],
    technologies: ["SAP IS-U", "SAP Energy Data Management", "SAP Customer Relationship Management", "SAP Analytics Cloud"],
    benefits: ["Operational Efficiency", "Customer Satisfaction", "Regulatory Compliance", "Cost Management", "Revenue Optimization", "Grid Optimization"],
    process: ["Utility Strategy", "Meter Management", "Billing Setup", "Customer Service Configuration", "Compliance Setup", "Training"],
    implementation: {
      planning: ["Utility Strategy", "Meter Strategy", "Billing Framework", "Customer Service Design", "Compliance Requirements"],
      execution: ["System Configuration", "Meter Setup", "Billing Implementation", "Customer Service Setup", "Compliance Configuration"],
      support: ["Utility Operations", "Meter Management", "Billing Support", "Customer Service", "Compliance Monitoring"]
    },
    industries: ["Electric Utilities", "Gas Utilities", "Water Utilities", "Energy Companies", "Municipal Utilities", "Renewable Energy"]
  },
  "km": {
    // icon: MessageSquare,
    title: "SAP Knowledge Management",
    // category: "Knowledge",
    description: "Content management, collaboration, and expertise location for knowledge sharing.",
    overview: "SAP Knowledge Management facilitates organizational learning through content management, collaboration tools, and expertise location, enabling effective knowledge sharing and retention.",
    features: ["Content Management", "Collaboration Tools", "Expertise Location", "Knowledge Base", "Document Search", "Community Building"],
    technologies: ["SAP Knowledge Management", "SAP Collaboration", "SAP Content Server", "SAP Enterprise Search"],
    benefits: ["Knowledge Sharing", "Collaboration Enhancement", "Expertise Utilization", "Reduced Redundancy", "Innovation Support", "Learning Culture"],
    process: ["Knowledge Strategy", "Content Architecture", "Collaboration Setup", "Search Configuration", "Community Building", "User Training"],
    implementation: {
      planning: ["Knowledge Strategy", "Content Framework", "Collaboration Requirements", "Search Strategy", "Community Design"],
      execution: ["Platform Setup", "Content Migration", "Collaboration Configuration", "Search Implementation", "Community Setup"],
      support: ["Content Management", "Community Facilitation", "Knowledge Curation", "User Support", "Platform Optimization"]
    },
    industries: ["Consulting", "Research", "Technology", "Professional Services", "Government", "Education"]
  },
  "ecc-cs": {
    title: "SAP ECC CS (Customer Service)",
    description: "Customer service management with incident tracking, service level agreements, and customer support workflows.",
    overview: "SAP ECC CS provides comprehensive customer service management capabilities, enabling organizations to handle customer incidents, manage service level agreements, and streamline customer support workflows with efficiency and transparency.",
    features: ["Incident Management", "Service Level Agreements", "Customer Support Workflows", "Service Analytics", "Knowledge Base Integration", "Multi-channel Support"],
    technologies: ["SAP ECC CS", "SAP Service Management", "SAP Customer Engagement", "SAP CRM Integration", "SAP Solution Manager"],
    benefits: ["Improved Customer Satisfaction", "Faster Resolution Times", "SLA Compliance", "Service Analytics", "Knowledge Sharing", "Streamlined Processes"],
    process: ["Service Strategy Definition", "Incident Management Setup", "SLA Configuration", "Workflow Design", "Knowledge Base Setup", "User Training"],
    implementation: {
      planning: ["Service Strategy", "Process Analysis", "SLA Framework", "Workflow Design", "Integration Requirements"],
      execution: ["System Configuration", "Incident Setup", "SLA Implementation", "Workflow Configuration", "Knowledge Base Setup"],
      support: ["Incident Management", "SLA Monitoring", "Process Optimization", "Performance Reporting", "Continuous Improvement"]
    },
    industries: ["Technology", "Telecommunications", "Manufacturing", "Financial Services", "Healthcare", "Retail"]
  },
  "digital-manufacturing": {
    title: "SAP Digital Manufacturing",
    description: "Digital manufacturing solutions with IoT integration, real-time monitoring, and smart factory capabilities.",
    overview: "SAP Digital Manufacturing enables smart factory operations through IoT integration, real-time monitoring, and digital twin technology, transforming traditional manufacturing into intelligent, connected operations.",
    features: ["IoT Integration", "Real-time Monitoring", "Smart Factory", "Digital Twin", "Predictive Maintenance", "Manufacturing Analytics"],
    technologies: ["SAP Digital Manufacturing", "SAP Manufacturing Execution", "SAP IoT", "SAP Edge Services", "SAP Analytics Cloud"],
    benefits: ["Operational Excellence", "Predictive Maintenance", "Quality Improvement", "Cost Reduction", "Real-time Visibility", "Smart Decision Making"],
    process: ["Digital Strategy", "IoT Infrastructure", "System Integration", "Analytics Implementation", "Digital Twin Setup", "User Training"],
    implementation: {
      planning: ["Digital Transformation Strategy", "IoT Architecture", "Data Strategy", "Analytics Framework", "Integration Planning"],
      execution: ["IoT Implementation", "System Integration", "Digital Twin Setup", "Analytics Configuration", "Monitoring Setup"],
      support: ["Operations Monitoring", "Predictive Maintenance", "Performance Optimization", "Analytics Support", "Continuous Innovation"]
    },
    industries: ["Automotive", "Aerospace", "Electronics", "Pharmaceuticals", "Food & Beverage", "Chemical"]
  },
  "cpi": {
    title: "SAP CPI (Cloud Platform Integration)",
    description: "Cloud-based integration platform for connecting SAP and non-SAP applications with pre-built connectors.",
    overview: "SAP CPI provides a comprehensive cloud-based integration platform that enables seamless connectivity between SAP and non-SAP applications, with pre-built connectors and flexible integration patterns.",
    features: ["Cloud Integration", "Pre-built Connectors", "API Management", "Message Processing", "Data Transformation", "Integration Monitoring"],
    technologies: ["SAP CPI", "SAP Cloud Platform", "SAP API Management", "SAP Integration Suite", "SAP Event Mesh"],
    benefits: ["Rapid Integration", "Reduced Complexity", "Scalable Architecture", "Cost Efficiency", "Real-time Connectivity", "Simplified Maintenance"],
    process: ["Integration Strategy", "Connector Configuration", "API Design", "Message Flow Design", "Testing", "Deployment"],
    implementation: {
      planning: ["Integration Architecture", "Connector Strategy", "API Framework", "Security Design", "Performance Planning"],
      execution: ["Platform Setup", "Connector Configuration", "API Development", "Message Flow Implementation", "Testing"],
      support: ["Integration Monitoring", "Performance Optimization", "Connector Updates", "Issue Resolution", "Enhancement Support"]
    },
    industries: ["All Industries", "Multi-system Environments", "Cloud-first Organizations", "Digital Transformation Projects", "Hybrid Landscapes"]
  },
  "authorization": {
    title: "SAP Authorization",
    description: "Comprehensive authorization management with role design, user provisioning, and access control.",
    overview: "SAP Authorization provides comprehensive security management through role-based access control, user provisioning, and authorization governance, ensuring data security and regulatory compliance.",
    features: ["Role Design", "User Provisioning", "Access Control", "Authorization Objects", "Segregation of Duties", "Compliance Reporting"],
    technologies: ["SAP Authorization", "SAP GRC Access Control", "SAP Identity Management", "SAP Role Management", "SAP Single Sign-On"],
    benefits: ["Enhanced Security", "Compliance Assurance", "Risk Mitigation", "Efficient User Management", "Audit Trail", "Automated Provisioning"],
    process: ["Security Assessment", "Role Design", "Authorization Configuration", "User Provisioning", "Testing", "Go-Live Support"],
    implementation: {
      planning: ["Security Framework", "Role Strategy", "Authorization Concept", "Compliance Requirements", "Integration Planning"],
      execution: ["Role Development", "Authorization Setup", "User Provisioning", "GRC Configuration", "Testing"],
      support: ["Role Maintenance", "User Administration", "Compliance Monitoring", "Security Reviews", "Access Certification"]
    },
    industries: ["Financial Services", "Healthcare", "Government", "Manufacturing", "Energy", "Telecommunications"]
  },
  "ewm": {
    title: "SAP EWM (Extended Warehouse Management)",
    description: "Advanced warehouse management with automated processes, labor management, and inventory optimization.",
    overview: "SAP EWM provides advanced warehouse management capabilities with automation, labor management, and inventory optimization, enabling efficient warehouse operations and supply chain excellence.",
    features: ["Warehouse Automation", "Labor Management", "Inventory Optimization", "Cross-docking", "Wave Management", "Yard Management"],
    technologies: ["SAP EWM", "SAP Warehouse Management", "SAP Transportation Management", "SAP Yard Management", "SAP Mobile"],
    benefits: ["Operational Efficiency", "Inventory Accuracy", "Labor Optimization", "Cost Reduction", "Real-time Visibility", "Customer Satisfaction"],
    process: ["Warehouse Strategy", "Process Design", "System Configuration", "Integration Setup", "Testing", "Go-Live Support"],
    implementation: {
      planning: ["Warehouse Strategy", "Process Analysis", "Layout Design", "Integration Planning", "Performance Metrics"],
      execution: ["System Configuration", "Process Implementation", "Integration Development", "Device Setup", "Testing"],
      support: ["Operations Support", "Performance Monitoring", "Process Optimization", "Training Programs", "Continuous Improvement"]
    },
    industries: ["Retail", "Manufacturing", "Distribution", "Automotive", "Consumer Goods", "E-commerce"]
  },
  "fico": {
    title: "SAP FICO (Finance & Controlling)",
    description: "Integrated finance and controlling solution combining financial accounting with management accounting.",
    overview: "SAP FICO integrates financial accounting and controlling functions, providing comprehensive financial management capabilities for accurate reporting, cost control, and strategic decision-making.",
    features: ["Financial Accounting", "Management Accounting", "Cost Center Management", "Profitability Analysis", "Financial Planning", "Integrated Reporting"],
    technologies: ["SAP FICO", "SAP New GL", "SAP Controlling", "SAP Profitability Analysis", "SAP Planning & Consolidation"],
    benefits: ["Financial Transparency", "Cost Control", "Real-time Reporting", "Compliance Management", "Strategic Insights", "Process Efficiency"],
    process: ["Financial Strategy", "Chart of Accounts Design", "Controlling Setup", "Integration Configuration", "Testing", "User Training"],
    implementation: {
      planning: ["Financial Framework", "Controlling Structure", "Reporting Strategy", "Integration Design", "Compliance Requirements"],
      execution: ["FICO Configuration", "Master Data Setup", "Integration Implementation", "Reporting Development", "Testing"],
      support: ["Financial Operations", "Month-end Support", "Reporting Maintenance", "Process Optimization", "Compliance Monitoring"]
    },
    industries: ["All Industries", "Manufacturing", "Services", "Retail", "Public Sector", "Financial Services"]
  },
  "ppqm": {
    title: "SAP PPQM (Production Planning & Quality Management)",
    description: "Integrated production planning with quality management for manufacturing excellence.",
    overview: "SAP PPQM combines production planning and quality management to ensure manufacturing excellence through integrated processes, quality controls, and continuous improvement.",
    features: ["Production Planning", "Quality Planning", "Inspection Management", "Manufacturing Analytics", "Quality Control", "Process Integration"],
    technologies: ["SAP PP", "SAP QM", "SAP Manufacturing Execution", "SAP Quality Management", "SAP Analytics Cloud"],
    benefits: ["Manufacturing Excellence", "Quality Assurance", "Cost Optimization", "Compliance Management", "Process Efficiency", "Customer Satisfaction"],
    process: ["Manufacturing Strategy", "Quality Planning", "Process Integration", "System Configuration", "Testing", "Training"],
    implementation: {
      planning: ["Manufacturing Framework", "Quality Strategy", "Process Integration", "Performance Metrics", "Compliance Planning"],
      execution: ["PPQM Configuration", "Quality Setup", "Process Implementation", "Integration Development", "Testing"],
      support: ["Production Support", "Quality Operations", "Process Monitoring", "Performance Optimization", "Continuous Improvement"]
    },
    industries: ["Automotive", "Pharmaceuticals", "Food & Beverage", "Electronics", "Aerospace", "Medical Devices"]
  },
  "c4c": {
    title: "SAP C4C (Cloud for Customer)",
    description: "Cloud-based customer relationship management with sales, service, and marketing capabilities.",
    overview: "SAP C4C provides comprehensive cloud-based CRM capabilities, integrating sales automation, service management, and marketing campaigns for enhanced customer relationships and business growth.",
    features: ["Sales Automation", "Service Management", "Marketing Campaigns", "Customer Analytics", "Mobile Access", "Integration Capabilities"],
    technologies: ["SAP C4C", "SAP Cloud for Customer", "SAP Marketing Cloud", "SAP Service Cloud", "SAP Analytics Cloud"],
    benefits: ["360-Degree Customer View", "Sales Performance", "Service Excellence", "Marketing Effectiveness", "Mobile Productivity", "Scalable Growth"],
    process: ["CRM Strategy", "Process Design", "System Configuration", "Data Migration", "Integration", "User Training"],
    implementation: {
      planning: ["CRM Strategy", "Process Analysis", "Data Architecture", "Integration Design", "Change Management"],
      execution: ["Cloud Configuration", "Data Migration", "Process Implementation", "Integration Development", "Testing"],
      support: ["CRM Operations", "User Support", "Process Optimization", "Performance Monitoring", "Feature Updates"]
    },
    industries: ["All Industries", "Professional Services", "Manufacturing", "Technology", "Healthcare", "Financial Services"]
  },
  "successfactors-ec": {
    title: "SAP SuccessFactors (EC)",
    description: "Employee Central cloud solution for core HR processes, organizational management, and employee data.",
    overview: "SAP SuccessFactors Employee Central provides comprehensive cloud-based HR management with core HR processes, organizational management, and employee lifecycle management for modern workforce needs.",
    features: ["Employee Data Management", "Organizational Management", "HR Analytics", "Employee Self-Service", "Workflow Management", "Global Compliance"],
    technologies: ["SAP SuccessFactors EC", "SAP Employee Central", "SAP SuccessFactors HXM", "SAP HR Analytics", "SAP Intelligent Services"],
    benefits: ["HR Digital Transformation", "Employee Experience", "Operational Efficiency", "Data-driven HR", "Global Compliance", "Scalable Growth"],
    process: ["HR Strategy", "Organizational Design", "System Configuration", "Data Migration", "Integration", "Change Management"],
    implementation: {
      planning: ["HR Transformation Strategy", "Organizational Framework", "Data Architecture", "Integration Design", "Change Strategy"],
      execution: ["EC Configuration", "Data Migration", "Process Implementation", "Integration Development", "Testing"],
      support: ["HR Operations", "Employee Support", "Process Optimization", "Analytics Support", "Feature Adoption"]
    },
    industries: ["All Industries", "Global Organizations", "Multi-location Companies", "Growth Companies", "Digital-first Organizations"]
  },
  "service-cloud-v2": {
    title: "SAP Service Cloud V2",
    description: "Next-generation service cloud platform with AI-powered customer service and field service management.",
    overview: "SAP Service Cloud V2 delivers next-generation customer service capabilities with AI-powered insights, omnichannel support, and intelligent service management for exceptional customer experiences.",
    features: ["AI-Powered Service", "Omnichannel Support", "Knowledge Management", "Service Analytics", "Field Service Management", "Customer Portal"],
    technologies: ["SAP Service Cloud V2", "SAP Conversational AI", "SAP Knowledge Central", "SAP Service Analytics", "SAP Field Service Management"],
    benefits: ["Customer Satisfaction", "Service Efficiency", "AI-driven Insights", "Omnichannel Experience", "Cost Optimization", "Service Innovation"],
    process: ["Service Strategy", "Channel Design", "AI Implementation", "Knowledge Setup", "Analytics Configuration", "User Training"],
    implementation: {
      planning: ["Service Strategy", "Channel Architecture", "AI Strategy", "Knowledge Framework", "Analytics Design"],
      execution: ["Platform Configuration", "AI Setup", "Channel Implementation", "Knowledge Base Development", "Analytics Configuration"],
      support: ["Service Operations", "AI Optimization", "Knowledge Management", "Performance Monitoring", "Feature Enhancement"]
    },
    industries: ["Technology", "Telecommunications", "Manufacturing", "Healthcare", "Financial Services", "Retail"]
  },
  "fsm": {
    title: "SAP FSM (Field Service Management)",
    description: "Mobile field service management with scheduling, dispatching, and technician enablement.",
    overview: "SAP FSM provides comprehensive field service management with mobile capabilities, intelligent scheduling, and technician enablement, optimizing field operations and customer service delivery.",
    features: ["Mobile Service", "Scheduling & Dispatching", "Work Order Management", "Technician Tools", "Customer Communication", "Service Analytics"],
    technologies: ["SAP FSM", "SAP Field Service Management", "SAP Mobile Services", "SAP Work Manager", "SAP Service Analytics"],
    benefits: ["Service Efficiency", "Customer Satisfaction", "Technician Productivity", "Cost Optimization", "Real-time Visibility", "Mobile Enablement"],
    process: ["Field Service Strategy", "Mobile Setup", "Scheduling Configuration", "Work Order Design", "Integration", "Training"],
    implementation: {
      planning: ["Field Service Strategy", "Mobile Architecture", "Process Design", "Integration Planning", "Performance Metrics"],
      execution: ["FSM Configuration", "Mobile Setup", "Scheduling Implementation", "Work Order Configuration", "Integration Development"],
      support: ["Field Operations", "Mobile Support", "Scheduling Optimization", "Performance Monitoring", "Continuous Improvement"]
    },
    industries: ["Utilities", "Manufacturing", "Technology", "Healthcare", "Telecommunications", "Construction"]
  },
  "abap": {
    title: "SAP ABAP",
    description: "Advanced Business Application Programming language for SAP development and customization.",
    overview: "SAP ABAP provides powerful development capabilities for SAP customization, enhancement, and integration, enabling organizations to tailor SAP solutions to their specific business requirements.",
    features: ["Custom Development", "Enhancement Framework", "Workflow Development", "Report Programming", "Interface Development", "Performance Optimization"],
    technologies: ["SAP ABAP", "SAP ABAP on HANA", "SAP ABAP Development Tools", "SAP Enhancement Framework", "SAP Web Dynpro"],
    benefits: ["Custom Solutions", "Business-specific Functionality", "Integration Capabilities", "Performance Optimization", "Maintenance Efficiency", "Technical Innovation"],
    process: ["Requirements Analysis", "Technical Design", "Development", "Testing", "Deployment", "Maintenance"],
    implementation: {
      planning: ["Development Strategy", "Technical Architecture", "Coding Standards", "Testing Framework", "Deployment Planning"],
      execution: ["Development Environment", "Custom Coding", "Enhancement Implementation", "Testing", "Documentation"],
      support: ["Code Maintenance", "Performance Monitoring", "Enhancement Support", "Technical Support", "Knowledge Transfer"]
    },
    industries: ["All Industries", "Complex Implementations", "Custom Requirements", "Integration-heavy Projects", "Performance-critical Applications"]
  },
  "sac": {
    title: "SAP Analytics Cloud (SAC)",
    description: "Comprehensive analytics platform with business intelligence, planning, and predictive analytics.",
    overview: "SAP Analytics Cloud provides a comprehensive analytics platform combining business intelligence, planning, and predictive analytics in a single cloud solution for data-driven decision making.",
    features: ["Business Intelligence", "Planning & Budgeting", "Predictive Analytics", "Data Visualization", "Story Building", "Mobile Analytics"],
    technologies: ["SAP Analytics Cloud", "SAP SAC", "SAP Predictive Analytics", "SAP Data Intelligence", "SAP HANA Cloud"],
    benefits: ["Unified Analytics", "Self-Service BI", "Collaborative Planning", "Predictive Insights", "Real-time Analytics", "Cloud Scalability"],
    process: ["Analytics Strategy", "Data Architecture", "Model Development", "Story Creation", "Planning Setup", "User Training"],
    implementation: {
      planning: ["Analytics Strategy", "Data Strategy", "Model Design", "Planning Framework", "User Requirements"],
      execution: ["SAC Setup", "Data Connection", "Model Development", "Story Building", "Planning Configuration"],
      support: ["Analytics Operations", "Model Maintenance", "User Support", "Performance Optimization", "Feature Adoption"]
    },
    industries: ["All Industries", "Data-driven Organizations", "Finance-focused Companies", "Strategic Planning Organizations", "Performance Management"]
  },
  "commerce": {
    title: "SAP Commerce",
    description: "Enterprise e-commerce platform with omnichannel capabilities and B2B/B2C support.",
    overview: "SAP Commerce provides a comprehensive e-commerce platform with omnichannel capabilities, supporting both B2B and B2C scenarios with advanced personalization and integration capabilities.",
    features: ["Omnichannel Commerce", "B2B & B2C Support", "Product Content Management", "Order Management", "Personalization", "Mobile Commerce"],
    technologies: ["SAP Commerce Cloud", "SAP Hybris", "SAP Customer Data Cloud", "SAP Commerce APIs", "SAP Marketing Cloud"],
    benefits: ["Omnichannel Experience", "Revenue Growth", "Customer Engagement", "Operational Efficiency", "Scalable Platform", "Market Agility"],
    process: ["Commerce Strategy", "Platform Setup", "Catalog Management", "Integration", "Personalization", "Go-Live"],
    implementation: {
      planning: ["Commerce Strategy", "Platform Architecture", "Integration Design", "Personalization Strategy", "Performance Planning"],
      execution: ["Platform Configuration", "Catalog Setup", "Integration Development", "Personalization Implementation", "Testing"],
      support: ["Commerce Operations", "Catalog Management", "Order Management", "Performance Monitoring", "Feature Enhancement"]
    },
    industries: ["Retail", "Manufacturing", "B2B Commerce", "Consumer Goods", "Fashion", "Electronics"]
  },
  "plm": {
    title: "SAP PLM (Product Lifecycle Management)",
    description: "Complete product lifecycle management from ideation to retirement with collaboration tools.",
    overview: "SAP PLM provides comprehensive product lifecycle management capabilities, supporting the entire product journey from ideation through development, manufacturing, and retirement with collaboration and compliance features.",
    features: ["Product Development", "Document Management", "Change Management", "Collaboration Tools", "Compliance Management", "Portfolio Management"],
    technologies: ["SAP PLM", "SAP Product Development", "SAP Document Management", "SAP Engineering Control Center", "SAP Recipe Management"],
    benefits: ["Innovation Acceleration", "Time-to-Market", "Compliance Assurance", "Collaboration Enhancement", "Cost Optimization", "Quality Improvement"],
    process: ["PLM Strategy", "Product Structure", "Document Management", "Change Control", "Collaboration Setup", "Training"],
    implementation: {
      planning: ["PLM Strategy", "Product Architecture", "Document Framework", "Change Management", "Collaboration Design"],
      execution: ["PLM Configuration", "Product Setup", "Document Management", "Change Control", "Collaboration Implementation"],
      support: ["PLM Operations", "Product Management", "Document Control", "Change Management", "Collaboration Support"]
    },
    industries: ["Manufacturing", "Automotive", "Aerospace", "Electronics", "Pharmaceuticals", "Consumer Goods"]
  },
  "tm": {
    title: "SAP TM (Transportation Management)",
    description: "Transportation planning and execution with carrier management and freight cost optimization.",
    overview: "SAP TM provides comprehensive transportation management capabilities with planning, execution, and optimization features for efficient logistics operations and cost control.",
    features: ["Transportation Planning", "Carrier Management", "Freight Optimization", "Track & Trace", "Load Building", "Settlement"],
    technologies: ["SAP TM", "SAP Transportation Management", "SAP Logistics Business Network", "SAP Event Mesh", "SAP Analytics Cloud"],
    benefits: ["Cost Optimization", "Operational Efficiency", "Carrier Performance", "Customer Service", "Visibility", "Compliance"],
    process: ["Transportation Strategy", "Carrier Setup", "Planning Configuration", "Execution Setup", "Optimization", "Training"],
    implementation: {
      planning: ["Transportation Strategy", "Carrier Strategy", "Network Design", "Optimization Framework", "Integration Planning"],
      execution: ["TM Configuration", "Carrier Setup", "Planning Implementation", "Execution Configuration", "Optimization Setup"],
      support: ["Transportation Operations", "Carrier Management", "Performance Monitoring", "Optimization Support", "Process Improvement"]
    },
    industries: ["Logistics", "Manufacturing", "Retail", "Automotive", "Consumer Goods", "Distribution"]
  },
  "spartacus": {
    title: "SAP Spartacus",
    description: "Modern Angular-based storefront framework for SAP Commerce with responsive design capabilities.",
    overview: "SAP Spartacus provides a modern, Angular-based storefront framework for SAP Commerce, enabling responsive, progressive web applications with flexible customization and extensibility.",
    features: ["Angular Framework", "Responsive Design", "Progressive Web App", "Headless Commerce", "Customizable Components", "Accessibility Support"],
    technologies: ["SAP Spartacus", "Angular", "SAP Commerce APIs", "Progressive Web App", "TypeScript"],
    benefits: ["Modern User Experience", "Mobile Optimization", "Development Flexibility", "Performance Optimization", "SEO-friendly", "Future-proof Architecture"],
    process: ["Frontend Strategy", "Design System", "Component Development", "API Integration", "PWA Setup", "Deployment"],
    implementation: {
      planning: ["Frontend Architecture", "Design Strategy", "Component Framework", "API Design", "Performance Strategy"],
      execution: ["Spartacus Setup", "Component Development", "API Integration", "Customization", "Testing"],
      support: ["Frontend Maintenance", "Component Updates", "Performance Optimization", "Feature Enhancement", "Technical Support"]
    },
    industries: ["E-commerce", "Retail", "B2B Commerce", "Consumer Brands", "Manufacturing", "Distribution"]
  },
  "migration": {
    title: "SAP Migration Types & Strategies",
    description: "Comprehensive SAP migration services including OS/DB migration, cloud migration, ECC to S/4HANA transformation, and database migration to HANA.",
    overview: "SAP Migration encompasses various types of system transitions, from simple hardware upgrades to complex transformations like ECC to S/4HANA conversion. Our comprehensive migration services ensure minimal downtime and maximum efficiency across all migration scenarios.",
    features: [
      "OS/DB Migration (Homogeneous & Heterogeneous)",
      "SAP System Migration to Cloud", 
      "SAP ECC to SAP S/4HANA Migration",
      "Database Migration to SAP HANA",
      "Unicode Migration",
      "SAP Landscape Consolidation",
      "SAP Data Migration Services"
    ],
    technologies: ["SAP Software Migration Service (SWPM)", "Migration Monitor", "DMO (Database Migration Option)", "SUM Tool", "LTMC", "SAP Data Services", "BODS"],
    benefits: ["Minimal Downtime", "Data Integrity", "Performance Optimization", "Cost Reduction", "Risk Mitigation", "Future-proof Architecture", "Compliance Assurance"],
    process: ["Migration Assessment", "Strategy Planning", "Environment Preparation", "Migration Execution", "Testing & Validation", "Go-Live Support"],
    implementation: {
      planning: ["Migration Strategy", "Risk Assessment", "Downtime Planning", "Resource Allocation", "Testing Framework"],
      execution: ["Environment Setup", "Data Migration", "System Migration", "Integration Testing", "Performance Validation"],
      support: ["Go-Live Support", "Post-Migration Optimization", "Issue Resolution", "Performance Monitoring", "User Support"]
    },
    industries: ["All Industries", "Enterprise Organizations", "Large Corporations", "Multi-national Companies", "Government", "Manufacturing"],
    migrationTypes: [
      {
        type: "OS/DB Migration (Homogeneous & Heterogeneous)",
        description: "System platform migration with same or different operating systems and databases",
        caseStudy: {
          client: "Global Manufacturing Corporation",
          challenge: "Legacy Windows Server 2008 with SQL Server 2012 reaching end of support, requiring platform upgrade for 50+ SAP systems across 25 countries.",
          solution: "Executed heterogeneous migration from Windows/SQL Server to Linux/HANA with zero data loss and minimal downtime using SWPM and Migration Monitor.",
          results: [
            { metric: "Migration Time", value: "72 hours", description: "Per system migration window" },
            { metric: "Performance Improvement", value: "40% faster", description: "Query response time" },
            { metric: "Cost Savings", value: "$2.5M annually", description: "Reduced licensing costs" },
            { metric: "Downtime", value: "<8 hours", description: "Per system go-live" }
          ]
        },
        subtypes: [
          {
            name: "Homogeneous Migration",
            details: "OS and DB remain the same (e.g., Linux + Oracle → Linux + Oracle). Used mostly for hardware upgrades or performance improvements.",
            technicalSpecs: "Same OS family and database version, simplified migration path with reduced risk."
          },
          {
            name: "Heterogeneous Migration", 
            details: "OS or DB changes (e.g., Windows + SQL Server → Linux + HANA). Requires SAP Software Migration Service (SWPM) and Migration Monitor.",
            technicalSpecs: "Cross-platform migration requiring data conversion, character set handling, and extensive testing."
          }
        ]
      },
      {
        type: "SAP System Migration to Cloud",
        description: "Migrate SAP workloads from on-premise to cloud or between cloud providers",
        caseStudy: {
          client: "Fortune 45 Retail Chain",
          challenge: "On-premise SAP ECC infrastructure aging, high maintenance costs, and need for global scalability for 200+ stores worldwide.",
          solution: "Migrated entire SAP landscape to AWS with S/4HANA conversion, implementing cloud-native architecture with auto-scaling and disaster recovery.",
          results: [
            { metric: "Infrastructure Cost", value: "45% reduction", description: "Annual infrastructure spend" },
            { metric: "Scalability", value: "10x capacity", description: "Peak season handling" },
            { metric: "Disaster Recovery", value: "15 min RTO", description: "Recovery time objective" },
            { metric: "Global Performance", value: "60% improvement", description: "Cross-region response time" }
          ]
        },
        subtypes: [
          {
            name: "On-Premise → Cloud",
            details: "Migrate SAP workloads from local data centers to cloud providers (AWS, Azure, GCP, SAP RISE).",
            technicalSpecs: "Lift-and-shift or re-platforming approach with cloud-native optimizations and security enhancements."
          },
          {
            name: "Cloud-to-Cloud Migration",
            details: "Shifting SAP systems between different cloud providers. Benefits: Scalability, cost optimization, disaster recovery.",
            technicalSpecs: "Cross-cloud migration with data synchronization, network reconfiguration, and service mapping."
          }
        ]
      },
      {
        type: "SAP ECC to SAP S/4HANA Migration",
        description: "The most common transformation today with three main approaches",
        caseStudy: {
          client: "International Automotive Manufacturer",
          challenge: "SAP ECC 6.0 system with heavy customizations, real-time reporting needs, and digital transformation requirements across 15 manufacturing plants.",
          solution: "Bluefield approach combining selective data migration with process reengineering, implementing Fiori apps and real-time analytics.",
          results: [
            { metric: "Process Efficiency", value: "55% improvement", description: "End-to-end process time" },
            { metric: "Data Processing", value: "20x faster", description: "Real-time analytics speed" },
            { metric: "User Productivity", value: "35% increase", description: "With Fiori UX" },
            { metric: "TCO Reduction", value: "$8M savings", description: "5-year total cost ownership" }
          ]
        },
        subtypes: [
          {
            name: "Brownfield (System Conversion)",
            details: "Existing ECC system upgraded to S/4HANA with minimal redesign. Keeps existing customizations & data.",
            technicalSpecs: "In-place conversion using SUM/DMO tools, maintains existing configuration with S/4HANA optimizations."
          },
          {
            name: "Greenfield (New Implementation)",
            details: "Fresh S/4HANA installation with new processes. Clean data & re-engineered business processes.",
            technicalSpecs: "New system build with best practices, selective data migration, and modern process design."
          },
          {
            name: "Bluefield (Selective Data Transition)",
            details: "Hybrid approach – migrate selected data & processes. Useful for phased rollouts or selective redesign.",
            technicalSpecs: "Combines new S/4HANA instance with selective data/configuration migration using advanced tools."
          }
        ]
      },
      {
        type: "Database Migration to SAP HANA",
        description: "Moving from legacy databases to SAP HANA in-memory database",
        caseStudy: {
          client: "Global Chemical Company",
          challenge: "Oracle database performance bottlenecks affecting production planning, month-end closing taking 5 days, and limited analytical capabilities.",
          solution: "Migrated from Oracle to SAP HANA using DMO with SUM tool, implemented HANA-optimized code and real-time reporting.",
          results: [
            { metric: "Query Performance", value: "100x faster", description: "Complex analytical queries" },
            { metric: "Month-end Closing", value: "5 days → 4 hours", description: "Financial period close" },
            { metric: "Storage Reduction", value: "70% less space", description: "Compressed data storage" },
            { metric: "TCO Savings", value: "$3.2M", description: "3-year database costs" }
          ]
        },
        subtypes: [
          {
            name: "Legacy DB to HANA",
            details: "Moving from legacy databases (Oracle, DB2, SQL Server, etc.) to SAP HANA in-memory database. Done using DMO (Database Migration Option) of SUM tool. Often a prerequisite step for S/4HANA migration.",
            technicalSpecs: "Automated migration with data compression, in-memory optimization, and performance tuning."
          }
        ]
      },
      {
        type: "Unicode Migration",
        description: "Convert SAP systems from non-Unicode to Unicode character encoding",
        caseStudy: {
          client: "Multinational Electronics Manufacturer",
          challenge: "Non-Unicode SAP system limiting global expansion, unable to support Asian markets with multi-byte characters, blocking S/4HANA roadmap.",
          solution: "Executed Unicode conversion with MDMP (Migration & Modernization Program), ensuring data integrity across 12 languages and 40 countries.",
          results: [
            { metric: "Global Support", value: "40 countries", description: "Multi-language operations" },
            { metric: "Data Integrity", value: "100%", description: "Character preservation" },
            { metric: "S/4HANA Ready", value: "Full compliance", description: "Prerequisites met" },
            { metric: "Migration Time", value: "36 hours", description: "Total downtime window" }
          ]
        },
        subtypes: [
          {
            name: "Non-Unicode to Unicode",
            details: "Conversion of SAP systems from non-Unicode to Unicode. Required for S/4HANA. Ensures multi-language data handling.",
            technicalSpecs: "Character set conversion with MDMP approach, data validation, and multi-language testing."
          }
        ]
      },
      {
        type: "SAP Landscape Consolidation / Carve-Out Migration",
        description: "Optimize SAP landscape through consolidation or separation",
        caseStudy: {
          client: "Diversified Industrial Conglomerate",
          challenge: "12 separate SAP instances across business units causing high maintenance costs, data silos, and complex integrations post-merger.",
          solution: "Consolidated 12 SAP systems into 3 regional instances with shared services model, implemented master data governance and unified reporting.",
          results: [
            { metric: "System Reduction", value: "12 → 3 instances", description: "Landscape simplification" },
            { metric: "Maintenance Cost", value: "60% reduction", description: "Annual operational cost" },
            { metric: "Data Quality", value: "95% accuracy", description: "Master data consistency" },
            { metric: "Integration Points", value: "80% fewer", description: "Interface complexity" }
          ]
        },
        subtypes: [
          {
            name: "Consolidation",
            details: "Multiple SAP systems → single SAP instance (reduces cost & complexity).",
            technicalSpecs: "System merger with master data harmonization, configuration consolidation, and user migration."
          },
          {
            name: "Carve-Out",
            details: "Splitting part of a system (e.g., divesting a business unit).",
            technicalSpecs: "Selective data extraction, new system provisioning, and business continuity planning."
          }
        ]
      },
      {
        type: "SAP Data Migration",
        description: "Comprehensive data migration services using modern tools and methodologies",
        caseStudy: {
          client: "Global Pharmaceutical Company",
          challenge: "Migration of 15 years of historical data from legacy ERP to new S/4HANA system, ensuring FDA compliance and data lineage tracking.",
          solution: "Implemented SAP Data Services with LTMC for structured migration, custom ETL processes for complex transformations, and comprehensive data validation.",
          results: [
            { metric: "Data Volume", value: "50TB migrated", description: "Historical data preserved" },
            { metric: "Data Quality", value: "99.8% accuracy", description: "Post-migration validation" },
            { metric: "Compliance", value: "FDA approved", description: "Regulatory standards met" },
            { metric: "Migration Speed", value: "2TB/hour", description: "Average throughput" }
          ]
        },
        subtypes: [
          {
            name: "ETL Data Migration",
            details: "Extraction, Transformation, and Loading (ETL) of business data into new systems. Often done using SAP Data Services, LTMC (Legacy Transfer Migration Cockpit), or BODS.",
            technicalSpecs: "Advanced ETL processes with data quality checks, transformation rules, and automated validation."
          }
        ]
      }
    ]
  },
  "flexframe": {
    title: "SAP Flexframe",
    description: "Integration framework for connecting SAP systems with third-party applications and legacy systems.",
    overview: "SAP Flexframe provides a flexible integration framework that enables seamless connectivity between SAP systems and third-party applications, facilitating data exchange and process integration.",
    features: ["System Integration", "Legacy System Connectivity", "Data Transformation", "Message Routing", "Protocol Support", "Error Handling"],
    technologies: ["SAP Flexframe", "SAP Process Integration", "SAP NetWeaver", "SAP Integration Suite", "SAP Cloud Platform"],
    benefits: ["Integration Flexibility", "Legacy Modernization", "Reduced Complexity", "Improved Connectivity", "Cost Efficiency", "Scalable Architecture"],
    process: ["Integration Strategy", "System Analysis", "Framework Setup", "Connection Development", "Testing", "Deployment"],
    implementation: {
      planning: ["Integration Architecture", "System Landscape", "Data Strategy", "Security Framework", "Performance Planning"],
      execution: ["Framework Setup", "Connection Development", "Data Mapping", "Testing", "Deployment"],
      support: ["Integration Monitoring", "Connection Maintenance", "Performance Optimization", "Issue Resolution", "Enhancement Support"]
    },
    industries: ["Complex IT Landscapes", "Legacy System Environments", "Multi-vendor Architectures", "Integration-heavy Projects", "Modernization Initiatives"]
  }
};

const SAPDetail = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  
  // Force re-render when moduleId changes
  useEffect(() => {
    // Scroll to top immediately and after a short delay to ensure it works
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [moduleId]);
  
  if (!moduleId || !sapModuleData[moduleId]) {
    return <Navigate to="/404" replace />;
  }

  const module = sapModuleData[moduleId];
  // const IconComponent = module.icon;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-4">
          {/* <Button 
            variant="ghost" 
            className="mb-8"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to SAP Modules
          </Button> */}
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* <div className="flex items-center gap-4 mb-6">
                <div className="bg-gradient-primary p-4 rounded-xl shadow-glow">
                  <IconComponent className="h-8 w-8 text-white" />
                </div>
                <Badge variant="secondary" className="text-sm">
                  {module.category}
                </Badge>
              </div> */}
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent pb-2">
                {module.title}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                {module.description}
              </p>
              
            </div>
            
            <div className="relative">
              <img 
                src={sapImage} 
                alt="SAP Dashboard"
                className="rounded-xl shadow-elegant w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-primary/20 rounded-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-foreground">
                Module Overview
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                {module.overview}
              </p>
              
              <h3 className="text-xl font-semibold mb-4 text-foreground">
                Key Benefits
              </h3>
              <div className="grid gap-3">
                {module.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Core Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {module.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-background/50 rounded-lg">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Technologies & Tools
          </h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {module.technologies.map((tech, index) => (
              <Badge key={index} variant="outline" className="px-4 py-2 text-sm">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Implementation Process
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-center text-primary">Planning Phase</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {module.implementation.planning.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-center text-primary">Execution Phase</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {module.implementation.execution.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-center text-primary">Support Phase</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {module.implementation.support.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Industry Applications
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {module.industries.map((industry, index) => (
              <div key={index} className="bg-background/50 p-4 rounded-lg text-center border border-border/50">
                <span className="text-sm font-medium text-foreground">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Types Section - Only shown for migration module */}
      {moduleId === 'migration' && module.migrationTypes && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
              Types of SAP Migration
            </h2>
            <div className="space-y-8 max-w-6xl mx-auto">
              {module.migrationTypes.map((migrationType, index) => (
                <Card key={index} className="bg-gradient-card border-0 shadow-elegant">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-primary flex items-center gap-3">
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                        {index + 1}
                      </Badge>
                      {migrationType.type}
                    </CardTitle>
                    <p className="text-muted-foreground">{migrationType.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Case Study Section */}
                      {migrationType.caseStudy && (
                        <div className="mb-6 p-6 bg-primary/5 border border-primary/20 rounded-lg">
                          <h4 className="font-bold text-primary mb-3 flex items-center gap-2">
                            <Award className="h-5 w-5" />
                            Success Story: {migrationType.caseStudy.client}
                          </h4>
                          
                          <div className="space-y-4">
                            <div>
                              <h5 className="font-semibold text-foreground mb-1">Challenge</h5>
                              <p className="text-sm text-muted-foreground">{migrationType.caseStudy.challenge}</p>
                            </div>
                            
                            <div>
                              <h5 className="font-semibold text-foreground mb-1">Solution</h5>
                              <p className="text-sm text-muted-foreground">{migrationType.caseStudy.solution}</p>
                            </div>
                            
                            <div>
                              <h5 className="font-semibold text-foreground mb-3">Results Achieved</h5>
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {migrationType.caseStudy.results.map((result, resultIndex) => (
                                  <div key={resultIndex} className="text-center p-3 bg-background/50 rounded-lg border border-border/50">
                                    <div className="text-lg font-bold text-primary">{result.value}</div>
                                    <div className="text-xs font-semibold text-foreground">{result.metric}</div>
                                    <div className="text-xs text-muted-foreground">{result.description}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Migration Approaches */}
                      <div>
                        <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                          <Settings className="h-4 w-4 text-primary" />
                          Migration Approaches
                        </h4>
                        <div className="space-y-4">
                          {migrationType.subtypes.map((subtype, subIndex) => (
                            <div key={subIndex} className="p-4 bg-background/50 rounded-lg border border-border/50">
                              <h5 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary" />
                                {subtype.name}
                              </h5>
                              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                                {subtype.details}
                              </p>
                              {subtype.technicalSpecs && (
                                <div className="mt-2 p-2 bg-muted/30 rounded border-l-4 border-primary">
                                  <p className="text-xs text-muted-foreground font-medium">
                                    <strong>Technical Specifications:</strong> {subtype.technicalSpecs}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Our Services Section */}
      <Services />

      <Footer />
    </div>
  );
};

export default SAPDetail;